import React, { useEffect, useMemo, useState } from 'react';
import {
  Paper,
  Typography,
  Button,
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Checkbox,
  FormControlLabel,
  Chip,
  Switch,
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import axios from 'axios';

interface ClassGroup {
  id: string;
  name: string;
  sequenceOrder: number;
  isActive: boolean;
}

const ClassGroupMaster: React.FC = () => {
  const [items, setItems] = useState<ClassGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingItem, setEditingItem] = useState<ClassGroup | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    sequenceOrder: 1,
    isActive: true,
  });
  const [search, setSearch] = useState('');

  useEffect(() => {
    fetchItems();
  }, []);

  const fetchItems = async () => {
    try {
      setLoading(true);
      const { data } = await axios.get('/system-settings/class-group');
      setItems(data || []);
    } catch (error) {
      console.error('Failed to fetch class groups:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredItems = useMemo(() => {
    if (!search) return items;
    const lowerSearch = search.toLowerCase();
    return items.filter(
      (item) =>
        item.name.toLowerCase().includes(lowerSearch)
    );
  }, [items, search]);

  const handleCreate = () => {
    setEditingItem(null);
    setFormData({
      name: '',
      sequenceOrder: items.length > 0 ? Math.max(...items.map(i => i.sequenceOrder)) + 1 : 1,
      isActive: true,
    });
    setDialogOpen(true);
  };

  const handleEdit = (item: ClassGroup) => {
    setEditingItem(item);
    setFormData({
      name: item.name,
      sequenceOrder: item.sequenceOrder,
      isActive: item.isActive,
    });
    setDialogOpen(true);
  };

  const handleToggleStatus = async (id: string, currentStatus: boolean) => {
    try {
      await axios.patch(`/system-settings/class-group/${id}/toggle-status`);
      setItems((prev) =>
        prev.map((item) =>
          item.id === id ? { ...item, isActive: !currentStatus } : item
        )
      );
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to toggle status');
      fetchItems();
    }
  };

  const handleSave = async () => {
    try {
      const payload = {
        name: formData.name.trim(),
        sequenceOrder: Number(formData.sequenceOrder),
        isActive: formData.isActive,
      };

      if (editingItem) {
        await axios.patch(`/system-settings/class-group/${editingItem.id}`, payload);
      } else {
        await axios.post('/system-settings/class-group', payload);
      }
      setDialogOpen(false);
      await fetchItems();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to save class group');
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Are you sure you want to delete this class group?')) {
      try {
        await axios.delete(`/system-settings/class-group/${id}`);
        await fetchItems();
      } catch (error: any) {
        alert(error.response?.data?.message || 'Cannot delete class group. It may contain classes.');
      }
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
        <Typography variant="h6">Class Groups</Typography>
        <Button variant="contained" startIcon={<AddIcon />} onClick={handleCreate}>
          Create
        </Button>
      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
        <TextField
          label="Search"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          size="small"
          sx={{ width: 300 }}
        />
      </Box>

      <TableContainer component={Paper}>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Group Name</TableCell>
              <TableCell>Sequence Order</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="right">Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredItems.length === 0 ? (
              <TableRow>
                <TableCell colSpan={4} align="center">
                  No class groups found
                </TableCell>
              </TableRow>
            ) : (
              filteredItems.map((item) => (
                <TableRow key={item.id}>
                  <TableCell>{item.name}</TableCell>
                  <TableCell>{item.sequenceOrder}</TableCell>
                  <TableCell>
                    <Switch
                      checked={item.isActive}
                      onChange={() => handleToggleStatus(item.id, item.isActive)}
                      color="primary"
                      size="small"
                    />
                  </TableCell>
                  <TableCell align="right">
                    <IconButton size="small" title="Edit" onClick={() => handleEdit(item)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton size="small" title="Delete" onClick={() => handleDelete(item.id)}>
                      <DeleteIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingItem ? 'Edit Class Group' : 'Create Class Group'}</DialogTitle>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, mt: 1 }}>
            <TextField
              label="Group Name"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              fullWidth
              required
            />
            <TextField
              label="Sequence Order"
              type="number"
              value={formData.sequenceOrder}
              onChange={(e) => setFormData({ ...formData, sequenceOrder: Number(e.target.value) })}
              fullWidth
              required
              helperText="Determines the display order of the group"
            />
            <FormControlLabel
              control={
                <Checkbox
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                />
              }
              label="Active"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDialogOpen(false)}>Cancel</Button>
          <Button onClick={handleSave} variant="contained" disabled={!formData.name}>
            Save
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ClassGroupMaster;
